// src/MainApp.js
import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import secureLocalStorage from "react-secure-storage";
//private
import Home from "./pages7-recipie-ai/Home";
import History from "./pages7-recipie-ai/History";
import CalHistory from "./pages7-recipie-ai/CalHistory";
import Blog from "./pages7-recipie-ai/Blog";
import CalorieCheck from "./pages7-recipie-ai/CalorieCheck";
import Profile from "./pages7-recipie-ai/Profile";
import BottomNav from "./components7/BottomNav";
import SubscriptionUpgrade from "./pages7-recipie-ai/SubscriptionUpgrade";

import Header from "./components7/Header";
import LoginPage from "./LoginPage";
import { SubscriptionProvider } from "./context/SubscriptionContext";
//assets
import "./MainApp.css";
import "./App.css";
import config from "./config.json";

//public
import PrivacyPolicy from "./Public/PrivacyPolicy";
import Contact from "./Public/Contact";
import PublicBottomNav from "./Public/BottomNav";
import PublicHeader from "./Public/Header";
///admin
import AdminHeader from "./adminComponents/Header";
import AdminBottomNav from "./adminComponents/BottomNav";
import AdminDashboard from "./adminPages/Dashboard";
import AdminContactus from "./adminPages/ContactUs";
import UserStats from "./adminPages/UserStats";

const getActiveStoredUser = () => {
  try {
    const isExplicitlyLoggedOut =
      secureLocalStorage.getItem("isLoggedOut") === true ||
      (typeof localStorage !== "undefined" && localStorage.getItem("isLoggedOut") === "true");
    if (isExplicitlyLoggedOut) return null;
  } catch (e) {}

  try {
    const rawSecure = secureLocalStorage.getItem("loggedInUser");
    if (rawSecure) {
      return typeof rawSecure === "string" ? JSON.parse(rawSecure) : rawSecure;
    }
  } catch (e) {}

  try {
    if (typeof localStorage !== "undefined") {
      const rawLocal = localStorage.getItem("loggedInUser");
      if (rawLocal) {
        return typeof rawLocal === "string" ? JSON.parse(rawLocal) : rawLocal;
      }
    }
  } catch (e) {}

  try {
    const rawGuest =
      secureLocalStorage.getItem("guestUser") ||
      (typeof localStorage !== "undefined" && localStorage.getItem("guestUser"));
    if (rawGuest) {
      return typeof rawGuest === "string" ? JSON.parse(rawGuest) : rawGuest;
    }
  } catch (e) {}

  return null;
};

const getRouteTabIndex = (pathname) => {
  if (pathname === "/" || pathname === "") return 0;
  if (pathname === "/caloriecheck") return 1;
  if (pathname === "/history" || pathname === "/calhistory") return 2;
  if (pathname === "/admin/dashboard") return 0;
  if (pathname === "/admin/contactus") return 1;
  if (pathname === "/admin/userstats") return 2;
  return -1;
};

const MainApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const initial = getActiveStoredUser();
    return initial || false;
  });
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const prevTabIdxRef = useRef(getRouteTabIndex(typeof window !== "undefined" ? window.location.pathname : ""));

  const currentTabIdx = getRouteTabIndex(location.pathname);
  const prevTabIdx = prevTabIdxRef.current;

  // Immediate synchronous calculation for instant first-frame transition
  let activeSlideDirection = location.state?.slideDirection || "";
  if (!activeSlideDirection && currentTabIdx !== -1 && prevTabIdx !== -1 && currentTabIdx !== prevTabIdx) {
    activeSlideDirection = currentTabIdx > prevTabIdx ? "slide-from-right" : "slide-from-left";
  }

  useEffect(() => {
    if (currentTabIdx !== -1) {
      prevTabIdxRef.current = currentTabIdx;
    }
    window.scrollTo(0, 0);
  }, [location.pathname, currentTabIdx]);

  const loggedInUserEmail =
    secureLocalStorage.getItem("loggedInUserEmail") ||
    (typeof localStorage !== "undefined" && localStorage.getItem("loggedInUserEmail")) ||
    (typeof isLoggedIn === "object" ? isLoggedIn?.email : "");

  useEffect(() => {
    try {
      const isExplicitlyLoggedOut =
        secureLocalStorage.getItem("isLoggedOut") === true ||
        (typeof localStorage !== "undefined" && localStorage.getItem("isLoggedOut") === "true");

      if (isExplicitlyLoggedOut) {
        setIsLoggedIn(false);
        return;
      }

      const activeUser = getActiveStoredUser();
      if (activeUser && (activeUser.email || activeUser.name)) {
        setIsLoggedIn(activeUser);
        setError(false);
      } else {
        setIsLoggedIn(false);
      }
    } catch (err) {
      console.warn("Session check error:", err);
    }
  }, []);

  const handleLogin = (userData) => {
    try {
      if (userData.email === "guest") {
        secureLocalStorage.setItem("guestUser", JSON.stringify(userData));
        secureLocalStorage.setItem("isLoggedOut", false);
        secureLocalStorage.setItem("loggedInUserEmail", "guest");
        secureLocalStorage.removeItem("loggedInUser");
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("guestUser", JSON.stringify(userData));
          localStorage.setItem("isLoggedOut", "false");
          localStorage.setItem("loggedInUserEmail", "guest");
          localStorage.removeItem("loggedInUser");
        }
      } else {
        secureLocalStorage.setItem("loggedInUser", JSON.stringify(userData));
        secureLocalStorage.setItem("loggedInUserEmail", userData.email);
        secureLocalStorage.setItem("rememberedEmail", userData.email);
        secureLocalStorage.setItem("isLoggedOut", false);
        secureLocalStorage.removeItem("guestUser");

        if (typeof localStorage !== "undefined") {
          localStorage.setItem("loggedInUser", JSON.stringify(userData));
          localStorage.setItem("loggedInUserEmail", userData.email);
          localStorage.setItem("rememberedEmail", userData.email);
          localStorage.setItem("isLoggedOut", "false");
          localStorage.removeItem("guestUser");
        }
      }
      setIsLoggedIn(userData);
      setError(false);
      navigate("");
    } catch (err) {
      setError(false);
      setIsLoggedIn(userData);
      navigate("");
    }
  };

  const NotFoundPage = () => (
    <div className="container text-center py-5">
      <h2 className="fw-bold">{config.apptitle || "Recipe AI"}</h2>
      <p className="text-secondary">Something went wrong. Please reload the app.</p>
      <button className="btn btn-success rounded-pill px-4" onClick={() => (window.location.href = "/")}>
        Reload
      </button>
    </div>
  );

  // Public Pages: No Login Required
  const PublicRoutes = () => (
    <>
      <PublicHeader title="Recipe AI" />
      <Routes>
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
      </Routes>
      <PublicBottomNav />
    </>
  );

  // Private Pages: Login or Guest Required
  const PrivateRoutes = () => {
    if (isLoggedIn) {
      return (
        <div className="main-content-layout">
          <Header title="Recipe AI" />
          <div className="page-body-container">
            <div key={location.pathname} className={`page-slide-wrapper ${activeSlideDirection}`}>
              <Routes location={location}>
                <Route path="/" element={<Home />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/manage" element={<Profile />} />
                <Route path="/support" element={<Profile />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/caloriecheck" element={<CalorieCheck />} />
                <Route path="/history" element={<History />} />
                <Route path="/calhistory" element={<CalHistory />} />
                <Route path="/upgrade" element={<SubscriptionUpgrade />} />
              </Routes>
            </div>
          </div>
          <BottomNav />
        </div>
      );
    } else {
      return <LoginPage onLogin={handleLogin} />;
    }
  };

  // Admin Pages: Admin Privileges Required
  const AdminRoutes = () => {
    if (loggedInUserEmail === "vbalakumar.cse@gmail.com") {
      return (
        <div className="main-content-layout">
          <AdminHeader title="Admin Dashboard" />
          <div className="page-body-container">
            <div key={location.pathname} className={`page-slide-wrapper ${activeSlideDirection}`}>
              <Routes location={location}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/contactus" element={<AdminContactus />} />
                <Route path="/admin/userstats" element={<UserStats />} />
              </Routes>
            </div>
          </div>
          <AdminBottomNav />
        </div>
      );
    } else {
      return <Navigate to="/" />;
    }
  };

  return (
    <div className="app">
      {error ? (
        <NotFoundPage />
      ) : (
        <ErrorBoundary>
          {window.location.pathname === "/contact" || window.location.pathname === "/privacypolicy" ? (
            <PublicRoutes />
          ) : window.location.pathname.includes("/admin") ? (
            <AdminRoutes />
          ) : (
            <PrivateRoutes />
          )}
        </ErrorBoundary>
      )}
    </div>
  );
};

const MainAppWrapper = () => (
  <Router>
    <SubscriptionProvider>
      <MainApp />
    </SubscriptionProvider>
  </Router>
);

export default MainAppWrapper;
